import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    OneDrive: any;
  }
}

@Component({
    selector: 'app-onedrive-modal',
    templateUrl: './onedrive-modal.component.html',
    styleUrls: ['./onedrive-modal.component.scss'],
    standalone: false
})
export class OneDriveModalComponent implements OnInit, OnDestroy {
  @Input() isVisible: boolean = false;
  @Output() fileSelected = new EventEmitter<{
    file: File;
    base64: string;
    fileName: string;
  }>();
  @Output() modalClosed = new EventEmitter<void>();

  private clientId = environment.oneDrive.clientId;
  private isOneDriveLoaded = false;
  isLoading = false;
  loadingMessage = '';

  constructor(private messageService: MessageService) {}

  ngOnInit(): void {
    this.loadOneDriveSDK();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private loadOneDriveSDK(): void {
    if (window.OneDrive) {
      this.isOneDriveLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.live.net/v7.2/OneDrive.js';
    script.onload = () => {
      this.isOneDriveLoaded = true;
      // console.log('OneDrive SDK loaded successfully');
    };
    script.onerror = () => {
      // console.error('Failed to load OneDrive SDK');
      this.messageService.add({
        severity: 'error',
        detail: 'Failed to load OneDrive SDK',
      });
    };
    document.head.appendChild(script);
  }

  openOneDrivePicker(): void {
    if (!this.isOneDriveLoaded) {
      this.messageService.add({
        severity: 'error',
        detail: 'OneDrive SDK not loaded yet. Please try again.',
      });
      return;
    }

    this.isLoading = true;
    this.loadingMessage = 'Opening OneDrive picker...';

    const odOptions = {
      clientId: this.clientId,
      action: 'download',
      multiSelect: false,
      advanced: {
        filter: '.pdf,.doc,.docx',
        // Remove redirectUri from here entirely, or set it properly
        // redirectUri: window.location.origin  // Remove this line
      },
      success: (files: any) => {
        this.handleOneDriveSuccess(files);
      },
      cancel: () => {
        this.isLoading = false;
        this.loadingMessage = '';
        // console.log('OneDrive picker cancelled');
      },
      error: (error: any) => {
        this.handleOneDriveError(error);
      },
    };

    try {
      window.OneDrive.open(odOptions);
    } catch (error) {
      // console.error('Error opening OneDrive picker:', error);
      this.isLoading = false;
      this.loadingMessage = '';
      this.messageService.add({
        severity: 'error',
        detail: 'Failed to open OneDrive picker',
      });
    }
  }

  private async handleOneDriveSuccess(files: any): Promise<void> {
    try {
      if (!files || !files.value || files.value.length === 0) {
        this.isLoading = false;
        this.loadingMessage = '';
        return;
      }

      const selectedFile = files.value[0];
      // console.log('Selected OneDrive file:', selectedFile);

      // Validate file type
      if (!this.isValidFileType(selectedFile.name)) {
        this.messageService.add({
          severity: 'error',
          detail: 'Please select a PDF, DOC, or DOCX file',
        });
        this.isLoading = false;
        this.loadingMessage = '';
        return;
      }

      // Validate file size (50MB limit)
      if (selectedFile.size > 50 * 1024 * 1024) {
        this.messageService.add({
          severity: 'error',
          detail: 'File size exceeds 50MB limit',
        });
        this.isLoading = false;
        this.loadingMessage = '';
        return;
      }

      this.loadingMessage = 'Downloading file from OneDrive...';

      // Download the file
      const downloadUrl =
        selectedFile['@microsoft.graph.downloadUrl'] ||
        selectedFile['@content.downloadUrl'] ||
        selectedFile.webUrl;
      if (!downloadUrl) {
        throw new Error('No download URL available');
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const file = new File([blob], selectedFile.name, {
        type: this.getMimeType(selectedFile.name),
      });

      this.loadingMessage = 'Processing file...';

      // Convert to base64
      const base64Data = await this.fileToBase64(file);

      // Emit the file selection event
      this.fileSelected.emit({
        file: file,
        base64: base64Data,
        fileName: selectedFile.name,
      });

      this.closeModal();
    } catch (error) {
      // console.error('Error processing OneDrive file:', error);
      this.messageService.add({
        severity: 'error',
        detail: 'Failed to download file from OneDrive',
      });
    } finally {
      this.isLoading = false;
      this.loadingMessage = '';
    }
  }

  private handleOneDriveError(error: any): void {
    // console.error('OneDrive error:', error);
    this.isLoading = false;
    this.loadingMessage = '';

    let errorMessage = 'An error occurred while accessing OneDrive';
    if (error.message) {
      errorMessage = error.message;
    }

    this.messageService.add({
      severity: 'error',
      detail: errorMessage,
    });
  }

  private isValidFileType(fileName: string): boolean {
    const validExtensions = ['.pdf', '.doc', '.docx'];
    const extension = fileName
      .toLowerCase()
      .substring(fileName.lastIndexOf('.'));
    return validExtensions.includes(extension);
  }

  private getMimeType(fileName: string): string {
    const extension = fileName
      .toLowerCase()
      .substring(fileName.lastIndexOf('.'));
    const mimeTypes: { [key: string]: string } = {
      '.pdf': 'application/pdf',
      '.doc': 'application/msword',
      '.docx':
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsDataURL(file);
    });
  }

  closeModal(): void {
    this.isVisible = false;
    this.isLoading = false;
    this.loadingMessage = '';
    this.modalClosed.emit();
  }

  // Alternative method using Microsoft Graph API (more robust)
  async openOneDriveWithGraph(): Promise<void> {
    try {
      // This method requires MSAL (Microsoft Authentication Library)
      // You would need to implement authentication first
      this.messageService.add({
        severity: 'info',
        detail: 'Graph API method requires additional authentication setup',
      });
    } catch (error) {
      // console.error('Graph API error:', error);
    }
  }
}
